import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { register as registerService } from 'services/authService';
import { authApi } from 'services/api';
import FormErrorBanner from 'components/errors/FormErrorBanner';
import InlineFieldError from 'components/errors/InlineFieldError';
import Icon from 'components/AppIcon';
import Logo from 'components/Logo';
import useSEO from 'hooks/useSEO';

function getPasswordStrength(pwd) {
  if (!pwd) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pwd?.length >= 8) score++;
  if (/[A-Z]/?.test(pwd)) score++;
  if (/[0-9]/?.test(pwd)) score++;
  if (/[^A-Za-z0-9]/?.test(pwd)) score++;
  const map = [
    { score: 0, label: '', color: '' },
    { score: 1, label: 'Weak', color: '#EF4444' },
    { score: 2, label: 'Fair', color: '#F97316' },
    { score: 3, label: 'Strong', color: '#EAB308' },
    { score: 4, label: 'Very Strong', color: '#22C55E' },
  ];
  return map?.[score];
}

export default function RegisterPage() {
  useSEO({
    title: 'Create Free Account',
    description: 'Sign up for Safe Dividend Tracker — free forever. Track dividend income, forecast DRIP compounding, and manage your options income in one place.',
    canonical: '/register',
  });

  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login: loginContext, isLoading: authLoading } = useAuth();

  // Where to send the user after registration / login (e.g. /affiliates/apply)
  const returnTo = location.state?.from || sessionStorage.getItem('after_login_dest') || '/user-dashboard';

  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  // Email verification pending state
  const [verificationPending, setVerificationPending] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, returnTo]);

  const strength = getPasswordStrength(form?.password);

  const requirements = [
    { label: 'At least 8 characters', met: form?.password?.length >= 8 },
    { label: 'One uppercase letter', met: /[A-Z]/?.test(form?.password) },
    { label: 'One number', met: /[0-9]/?.test(form?.password) },
    { label: 'One special character', met: /[^A-Za-z0-9]/?.test(form?.password) },
  ];

  const setField = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setFieldErrors((p) => ({ ...p, [key]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    let valid = true;
    if (!form?.firstName?.trim()) { errors.firstName = 'First name is required.'; valid = false; }
    if (!form?.lastName?.trim()) { errors.lastName = 'Last name is required.'; valid = false; }
    if (!form?.email?.trim()) { errors.email = 'Email address is required.'; valid = false; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) { errors.email = 'Please enter a valid email address.'; valid = false; }
    if (!form?.password) { errors.password = 'Password is required.'; valid = false; }
    else if (form?.password?.length < 8) { errors.password = 'Password must be at least 8 characters.'; valid = false; }
    if (!form?.confirmPassword) { errors.confirmPassword = 'Please confirm your password.'; valid = false; }
    else if (form?.password !== form?.confirmPassword) { errors.confirmPassword = 'Passwords do not match.'; valid = false; }
    if (!agreeTerms) { errors.terms = 'You must agree to the Terms of Service.'; valid = false; }
    setFieldErrors(errors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormError(null);
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      const refCode = localStorage.getItem('sdt_ref') || new URLSearchParams(window.location.search).get('ref') || undefined;
      const res = await registerService({
        first_name: form?.firstName,
        last_name: form?.lastName,
        email: form?.email,
        password: form?.password,
        platform: 'web',
        ...(refCode ? { ref: refCode } : {}),
      });
      // New flow: server returns requiresVerification=true, no JWT
      if (res?.requiresVerification) {
        localStorage.removeItem('sdt_ref');
        // Persist destination so verify-email page can redirect correctly
        if (returnTo !== '/user-dashboard') {
          try { sessionStorage.setItem('after_login_dest', returnTo); } catch {}
        }
        setRegisteredEmail(res?.email || form?.email);
        setVerificationPending(true);
      } else if (res?.token && res?.user) {
        localStorage.removeItem('sdt_ref');
        try { sessionStorage.removeItem('after_login_dest'); } catch {}
        loginContext(res.user, res.token);
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || '';
      if (status === 409 || msg?.toLowerCase()?.includes('exist') || msg?.toLowerCase()?.includes('duplicate')) {
        setFormError('An account with this email already exists. Try signing in instead.');
      } else if (status === 422) {
        const serverErrors = err?.response?.data?.errors || {};
        const mapped = {};
        if (serverErrors?.email) mapped.email = Array.isArray(serverErrors?.email) ? serverErrors?.email?.[0] : serverErrors?.email;
        if (serverErrors?.password) mapped.password = Array.isArray(serverErrors?.password) ? serverErrors?.password?.[0] : serverErrors?.password;
        setFieldErrors((p) => ({ ...p, ...mapped }));
        setFormError(err?.response?.data?.message || 'Please fix the errors below.');
      } else {
        setFormError(err?.response?.data?.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendMessage('');
    try {
      await authApi.resendVerification({ email: registeredEmail });
      setResendMessage('A new verification email has been sent. Check your inbox.');
    } catch {
      setResendMessage('Could not send email. Please try again shortly.');
    } finally {
      setResendLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#2E7D32', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  // ── Email verification pending ───────────────────────────────────────────────
  if (verificationPending) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: 'var(--color-background)' }}>
        <div className="w-full text-center" style={{ maxWidth: '420px' }}>
          <div className="rounded-xl border p-8" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: '0 4px 24px -4px rgba(0,0,0,0.08)' }}>
            {/* Icon */}
            <div className="flex justify-center mb-5">
              <div
                className="flex items-center justify-center w-16 h-16 rounded-full"
                style={{ backgroundColor: 'rgba(37,99,235,0.1)' }}
              >
                <Icon name="Mail" size={32} style={{ color: '#2563eb' }} />
              </div>
            </div>

            <h1 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>
              Check your inbox
            </h1>
            <p className="text-sm mb-1" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>
              We sent a verification link to:
            </p>
            <p className="text-sm font-semibold mb-5" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>
              {registeredEmail}
            </p>
            <p className="text-sm mb-6" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>
              Click the link in the email to verify your address and activate your account. The link expires in 24 hours. Can't find it? Check your spam or junk folder.
            </p>

            {/* Resend */}
            {resendMessage ? (
              <p className="text-sm mb-4" style={{ color: resendMessage.includes('sent') ? 'var(--color-success)' : 'var(--color-error)', fontFamily: 'var(--font-caption)' }}>
                {resendMessage}
              </p>
            ) : null}
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="w-full flex items-center justify-center gap-2 rounded-lg border text-sm font-medium transition-all hover:opacity-80 disabled:opacity-60 mb-4"
              style={{ height: '44px', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)', backgroundColor: 'var(--color-muted)' }}
            >
              {resendLoading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="RefreshCw" size={16} />}
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </button>

            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              Already verified?{' '}
              <Link to="/login" className="font-medium" style={{ color: '#2E7D32' }}>Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="w-full" style={{ maxWidth: '420px' }}>
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="mb-4 inline-block">
            <Logo size={56} />
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Create Your Account</h1>
          <p className="text-sm" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>Start tracking your dividend income</p>
        </div>

        {/* Card */}
        <div className="rounded-xl border p-6 md:p-8" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: '0 4px 24px -4px rgba(0,0,0,0.08)' }}>
          <FormErrorBanner error={formError} onDismiss={() => setFormError(null)} />

          <form onSubmit={handleSubmit} noValidate>
            {/* Name row */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>First Name</label>
                <input
                  type="text"
                  value={form?.firstName}
                  onChange={(e) => setField('firstName', e?.target?.value)}
                  placeholder="John"
                  autoComplete="given-name"
                  className="input-base"
                  style={{ backgroundColor: 'var(--color-background)', borderColor: fieldErrors?.firstName ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', height: '44px' }}
                />
                <InlineFieldError message={fieldErrors?.firstName} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>Last Name</label>
                <input
                  type="text"
                  value={form?.lastName}
                  onChange={(e) => setField('lastName', e?.target?.value)}
                  placeholder="Doe"
                  autoComplete="family-name"
                  className="input-base"
                  style={{ backgroundColor: 'var(--color-background)', borderColor: fieldErrors?.lastName ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', height: '44px' }}
                />
                <InlineFieldError message={fieldErrors?.lastName} />
              </div>
            </div>

            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>Email Address</label>
              <input
                type="email"
                value={form?.email}
                onChange={(e) => setField('email', e?.target?.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="input-base"
                style={{ backgroundColor: 'var(--color-background)', borderColor: fieldErrors?.email ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', height: '48px' }}
              />
              <InlineFieldError message={fieldErrors?.email} />
            </div>

            {/* Password */}
            <div className="mb-2">
              <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form?.password}
                  onChange={(e) => setField('password', e?.target?.value)}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                  className="input-base pr-12"
                  style={{ backgroundColor: 'var(--color-background)', borderColor: fieldErrors?.password ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', height: '48px' }}
                />
                <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:opacity-70">
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} color="#94A3B8" />
                </button>
              </div>
              <InlineFieldError message={fieldErrors?.password} />
            </div>

            {/* Strength bar */}
            {form?.password && (
              <div className="mb-3">
                <div className="flex gap-1 mb-1">
                  {[1,2,3,4]?.map((i) => (
                    <div key={i} className="flex-1 h-1.5 rounded-full transition-all" style={{ backgroundColor: i <= strength?.score ? strength?.color : 'var(--color-border)' }} />
                  ))}
                </div>
                {strength?.label && <p className="text-xs" style={{ fontFamily: 'var(--font-caption)', color: strength?.color }}>{strength?.label}</p>}
              </div>
            )}

            {/* Requirements checklist */}
            {form?.password && (
              <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
                <div className="grid grid-cols-2 gap-1">
                  {requirements?.map((req) => (
                    <div key={req?.label} className="flex items-center gap-1.5">
                      <Icon name={req?.met ? 'CheckCircle2' : 'Circle'} size={12} color={req?.met ? '#22C55E' : '#64748B'} />
                      <span className="text-xs" style={{ fontFamily: 'var(--font-caption)', color: req?.met ? '#22C55E' : '#64748B' }}>{req?.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Password */}
            <div className="mb-5">
              <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={form?.confirmPassword}
                  onChange={(e) => setField('confirmPassword', e?.target?.value)}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                  className="input-base pr-12"
                  style={{ backgroundColor: 'var(--color-background)', borderColor: fieldErrors?.confirmPassword ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', height: '48px' }}
                />
                <button type="button" onClick={() => setShowConfirm((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:opacity-70">
                  <Icon name={showConfirm ? 'EyeOff' : 'Eye'} size={18} color="#94A3B8" />
                </button>
              </div>
              <InlineFieldError message={fieldErrors?.confirmPassword} />
            </div>

            {/* Terms */}
            <div className="mb-6">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => { setAgreeTerms(e?.target?.checked); setFieldErrors((p) => ({ ...p, terms: '' })); }}
                  className="w-4 h-4 mt-0.5 rounded flex-shrink-0 cursor-pointer"
                  style={{ accentColor: '#2E7D32' }}
                />
                <span className="text-sm" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#2E7D32' }}>Terms of Service</a>{' '}and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline" style={{ color: '#2E7D32' }}>Privacy Policy</a>
                </span>
              </div>
              <InlineFieldError message={fieldErrors?.terms} />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ height: '48px', backgroundColor: '#2E7D32', color: '#FFFFFF', fontFamily: 'var(--font-caption)', fontSize: '1rem' }}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <Icon name="UserPlus" size={18} color="#FFFFFF" />
                  Create Free Account
                </>
              )}
            </button>
          </form>

          {/* Sign in link */}
          <p className="text-center text-sm mt-6" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>
            Already have an account?{' '}
            <Link to="/login" className="font-medium transition-opacity hover:opacity-80" style={{ color: '#2E7D32' }}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
